import React, { useMemo, useState } from 'react';
import { Button, DatePicker, InputNumber, Modal, Popover, Typography, message } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faCircleCheck,
  faCircleInfo,
  faShieldCheck,
} from '@fortawesome/pro-solid-svg-icons';
import type { AnalyticsKpiDashboardPayload, FounderTarget } from '../../services/analytics';
import {
  buildFounderCards,
  buildFounderChanges,
  comparisonLabel,
  founderDelta,
  founderStatus,
  formatFounderValue,
  statusBands,
  targetFromInput,
  targetToInput,
  type FounderCardData,
  type FounderChangeTone,
  type FounderStatus,
} from './founderMetrics';
import './FounderSummary.css';

const { Text } = Typography;

const STATUS_LABEL: Record<FounderStatus, string> = { good: 'Good', watch: 'Watch', bad: 'Bad' };

interface FounderSummaryProps {
  data: AnalyticsKpiDashboardPayload;
  previousData?: AnalyticsKpiDashboardPayload;
  targets: FounderTarget[];
  canEditTargets: boolean;
  onSaveTarget: (metricId: string, target: number, dueDate: string | null) => Promise<void>;
  onOpenDetails: () => void;
}

function Sparkline({ values, tone }: { values: number[]; tone: 'good' | 'watch' | 'bad' | 'neutral' }) {
  const width = 320;
  const height = 44;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - 4 - ((value - min) / span) * (height - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className={`founder-sparkline founder-sparkline-${tone}`} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function StatusPill({ card }: { card: FounderCardData }) {
  if (card.value === null) {
    return <span className="founder-pill founder-pill-muted">Not tracked yet</span>;
  }
  if (!card.status) return null;
  return <span className={`founder-pill founder-pill-${card.status}`}>{STATUS_LABEL[card.status]}</span>;
}

function targetSummary(card: FounderCardData): string {
  if (!card.target) return 'No target yet';
  const value = formatFounderValue(card.target.target, card.definition.kind);
  const by = card.target.dueDate ? ` by ${dayjs(card.target.dueDate).format('MMM D')}` : '';
  return `Target: ${value}${by}`;
}

function HowMeasured({
  card,
  canEditTargets,
  onEditTarget,
}: {
  card: FounderCardData;
  canEditTargets: boolean;
  onEditTarget: () => void;
}) {
  const { howMeasured } = card.definition;
  const target = card.target;
  return (
    <div className="founder-measured">
      <div className="founder-eyebrow">How it’s measured</div>
      <div className="founder-measured-title">{card.definition.title}</div>
      <div className="founder-measured-section">
        <div className="founder-measured-label">Counts</div>
        <p>{howMeasured.counts}</p>
      </div>
      <div className="founder-measured-section">
        <div className="founder-measured-label">Doesn’t count</div>
        <p>{howMeasured.doesntCount}</p>
      </div>
      <div className="founder-measured-section">
        <div className="founder-measured-label">Why it matters</div>
        <p>{howMeasured.whyItMatters}</p>
      </div>
      <div className="founder-measured-target">
        <div>
          <div className="founder-measured-label">Your target</div>
          <div className="founder-measured-target-value">
            {target
              ? `${formatFounderValue(target.target, card.definition.kind)}${target.dueDate ? ` by ${dayjs(target.dueDate).format('MMM D, YYYY')}` : ''}`
              : 'Not set'}
          </div>
        </div>
        {canEditTargets && (
          <Button type="text" size="small" className="founder-link" onClick={onEditTarget}>
            {target ? 'Edit target' : 'Set target'}
          </Button>
        )}
      </div>
      <div className="founder-measured-source">Source: {howMeasured.source}</div>
    </div>
  );
}

function FounderCard({
  card,
  comparison,
  canEditTargets,
  onEditTarget,
}: {
  card: FounderCardData;
  comparison: string;
  canEditTargets: boolean;
  onEditTarget: () => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { definition } = card;
  const delta = founderDelta(card.value, card.previous, definition.kind, comparison);
  const sparkTone = card.status || 'neutral';
  return (
    <article className="founder-card" data-testid={`founder-card-${definition.id}`}>
      <div className="founder-card-top">
        <span className="founder-eyebrow">{definition.question}</span>
        <StatusPill card={card} />
      </div>
      <h3 className="founder-card-title">{definition.title}</h3>
      <div className="founder-card-value-row">
        <span className={`founder-card-value${card.value === null ? ' founder-card-value-empty' : ''}`}>
          {formatFounderValue(card.value, definition.kind)}
        </span>
        {delta && <span className={`founder-delta founder-delta-${delta.direction}`}>{delta.text}</span>}
        {card.context && <span className="founder-delta founder-delta-flat">{card.context}</span>}
      </div>
      {card.trend ? <Sparkline values={card.trend} tone={sparkTone} /> : <div className="founder-card-rule" />}
      <p className="founder-card-description">{definition.description}</p>
      {card.value === null && <p className="founder-card-note">{definition.unavailableNote}</p>}
      <div className="founder-card-footer">
        <span>{targetSummary(card)}</span>
        <Popover
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          classNames={{ root: 'founder-popover' }}
          content={(
            <HowMeasured
              card={card}
              canEditTargets={canEditTargets}
              onEditTarget={() => {
                setPopoverOpen(false);
                onEditTarget();
              }}
            />
          )}
        >
          <button type="button" className="founder-link">How it’s measured ›</button>
        </Popover>
      </div>
    </article>
  );
}

function TargetDialog({
  card,
  onCancel,
  onSave,
}: {
  card: FounderCardData;
  onCancel: () => void;
  onSave: (target: number, dueDate: string | null) => Promise<void>;
}) {
  const { kind, title } = card.definition;
  const [input, setInput] = useState<number | null>(card.target ? targetToInput(card.target.target, kind) : null);
  const [dueDate, setDueDate] = useState<Dayjs | null>(card.target?.dueDate ? dayjs(card.target.dueDate) : null);
  const [saving, setSaving] = useState(false);
  const target = targetFromInput(input, kind);
  const bands = target === null ? null : statusBands(target, kind);
  const today = target === null ? null : founderStatus(card.value, target);

  async function save() {
    if (target === null) return;
    setSaving(true);
    try {
      await onSave(target, dueDate ? dueDate.format('YYYY-MM-DD') : null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      width={480}
      title={(
        <div>
          <div className="founder-eyebrow">Founder summary</div>
          <div className="founder-dialog-title">Set target · {title}</div>
        </div>
      )}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" type="text" onClick={onCancel}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} disabled={target === null} onClick={save}>Save target</Button>,
      ]}
      destroyOnHidden
    >
      <p className="founder-dialog-lead">
        Pick a number you’d be happy with. The card turns Good when you’re at or above it, Watch when you’re within 10%, and Bad below that.
      </p>
      <div className="founder-dialog-fields">
        <label>
          <span>Target</span>
          <InputNumber
            autoFocus
            value={input}
            min={0}
            max={kind === 'percent' ? 100 : undefined}
            precision={kind === 'percent' ? 1 : 0}
            onChange={(value) => setInput(typeof value === 'number' ? value : null)}
            suffix={kind === 'percent' ? '%' : 'couples'}
            style={{ width: '100%' }}
          />
        </label>
        <label>
          <span>By (optional)</span>
          <DatePicker value={dueDate} onChange={setDueDate} format="MMM D, YYYY" style={{ width: '100%' }} />
        </label>
      </div>
      {bands && (
        <div className="founder-dialog-preview">
          <div className="founder-measured-label">With this target</div>
          <div><span className="founder-dot founder-dot-good" />Good: {bands.good}</div>
          <div><span className="founder-dot founder-dot-watch" />Watch: {bands.watch}</div>
          <div><span className="founder-dot founder-dot-bad" />Bad: {bands.bad}</div>
          {card.value !== null && today && (
            <div className="founder-dialog-today">
              Today: {formatFounderValue(card.value, kind)} → {STATUS_LABEL[today]}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** What each journey percentage is a share of (the server's denominators). */
const JOURNEY_RATE_OF: Record<string, string> = {
  signed_up: 'of installs',
  paired: 'of sign-ups',
  first_shared_moment: 'of new couples',
  week_4: 'of couples paired 4 weeks ago',
  paying: 'of new couples',
};

const CHANGE_ICON: Record<FounderChangeTone, typeof faArrowTrendUp> = {
  up: faArrowTrendUp,
  down: faArrowTrendDown,
  good: faCircleCheck,
  info: faCircleInfo,
};

const FounderSummary: React.FC<FounderSummaryProps> = ({
  data,
  previousData,
  targets,
  canEditTargets,
  onSaveTarget,
  onOpenDetails,
}) => {
  const [editing, setEditing] = useState<string | null>(null);
  const cards = useMemo(() => buildFounderCards(data, previousData, targets), [data, previousData, targets]);
  const changes = useMemo(() => buildFounderChanges(cards), [cards]);
  const comparison = comparisonLabel(data.period.days);
  const journey = data.journey || [];
  const journeyMax = Math.max(1, ...journey.map((step) => step.value || 0));
  const editingCard = cards.find((card) => card.definition.id === editing) || null;
  const refreshedAt = data.snapshot?.refreshedAt ? dayjs(data.snapshot.refreshedAt).format('MMM D, HH:mm') : null;

  return (
    <div className="founder-summary">
      <section className="founder-grid" aria-label="Founder summary">
        {cards.map((card) => (
          <FounderCard
            key={card.definition.id}
            card={card}
            comparison={comparison}
            canEditTargets={canEditTargets}
            onEditTarget={() => setEditing(card.definition.id)}
          />
        ))}
      </section>

      <section className="founder-lower">
        <article className="founder-panel founder-journey">
          <h3>Couple journey</h3>
          <Text className="founder-panel-lead">
            Where people drop off on the way to becoming a happy, paying couple. This period.
          </Text>
          {journey.length === 0 && <Text className="founder-panel-lead">Not available for this period yet.</Text>}
          <div className="founder-journey-rows">
            {journey.map((step, index) => {
              const tracked = step.value !== null;
              return (
                <div className="founder-journey-row" key={step.id} title={step.detail}>
                  <span className="founder-journey-label">{step.label}</span>
                  <span className={`founder-journey-bar${tracked ? '' : ' founder-journey-bar-empty'}`}>
                    {tracked && <span style={{ width: `${Math.max(1.5, ((step.value || 0) / journeyMax) * 100)}%` }} />}
                  </span>
                  <span className="founder-journey-value">{tracked ? formatFounderValue(step.value, 'count') : '—'}</span>
                  <span className="founder-journey-rate">
                    {!tracked
                      ? 'Not tracked yet'
                      : index > 0 && step.rateOfPrevious !== null
                        ? `${formatFounderValue(step.rateOfPrevious, 'percent')} ${JOURNEY_RATE_OF[step.id] || ''}`.trim()
                        : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </article>

        <article className="founder-panel founder-changes">
          <h3>What changed</h3>
          {changes.length === 0 && <Text className="founder-panel-lead">Not enough data to compare yet.</Text>}
          <ul>
            {changes.map((change) => (
              <li key={change.text}>
                <FontAwesomeIcon icon={CHANGE_ICON[change.tone]} className={`founder-change-${change.tone}`} />
                <span>{change.text}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <footer className="founder-data-note">
        <FontAwesomeIcon icon={faShieldCheck} />
        <span>
          Data through {dayjs(data.period.end).format('MMM D')} (complete days only) · unique couples, not couple-days ·
          test accounts and admin-granted Plus excluded{refreshedAt ? ` · refreshed ${refreshedAt}` : ''}
        </span>
        <button type="button" className="founder-link" onClick={onOpenDetails}>Open details ›</button>
      </footer>

      {editingCard && (
        <TargetDialog
          card={editingCard}
          onCancel={() => setEditing(null)}
          onSave={async (target, dueDate) => {
            try {
              await onSaveTarget(editingCard.definition.id, target, dueDate);
              message.success('Target saved');
              setEditing(null);
            } catch (error) {
              message.error((error as Error)?.message || 'Could not save the target');
            }
          }}
        />
      )}
    </div>
  );
};

export default FounderSummary;
